import { useUser, useUserTimeZone } from '@ztechapps/common.hook'
import { formatConstants, formatDate, timeFromNow } from '@ztechapps/common.utils'
import { HoverContainer, HoverMenuContainer } from '@ztechapps/webui.menu'
import styles from './index.module.scss'
import { HorizontalStack, Icon, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import React from 'react'
import type { Activity } from '@ztechapps/common.contract'
import { useActivityContext } from './Context'
import isEmpty from 'lodash/isEmpty'
import { AddNewActivityButton } from './AddNewActivityButton'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { Stack } from 'react-bootstrap'
import { RichTextDisplay, UserAvatar } from '@ztechapps/webui.input'

export const ActivityList = () => {
  const { activities, showInput } = useActivityContext()
  if (isEmpty(activities) && !showInput) {
    return <AddNewActivityButton text="No activity found, click here to add new one!" />
  }

  return activities.map((e, index) => <ActivityItem key={index} {...e} />)
}

export const ActivityItem = ({ _id, by, updatedAt, activity, isDeleted }: Activity) => {
  const { components, disabled } = useActivityContext()
  const timeZone = useUserTimeZone()
  const time = timeFromNow(updatedAt, timeZone)
  const DeleteActivity = components.deleteActivityButton
  const UpdateActivity = components.updateRemarksInput
  const { user } = useUser()
  const [showEdit, setShowEdit] = React.useState(false)

  let deletedTextStyle = ''
  if (isDeleted) {
    deletedTextStyle = 'fst-italic text-decoration-line-through'
  }

  return (
    <HoverContainer>
      <div className={`${styles.activityItem} d-flex  mt-2`}>
        <small className={`${styles.activityLabel} `}></small>
        <ShowIf condition={!isEmpty(by?.profile)}>
          <div className={`${styles.activityBadge}  `}>
            <UserAvatar size={25} profile={by?.profile} />
          </div>
        </ShowIf>

        <ShowIf condition={isEmpty(by?.profile)}>
          <div className={`${styles.eventBadge}  `}>
            <small>
              <Icon name="circle-dot" />
            </small>
          </div>
        </ShowIf>

        <div className={`${styles.activityContent} flex-fill`}>
          <HorizontalStack gap={2}>
            <div>
              <TooltipPopOver tooltipBody={`Updated at ${formatDate(updatedAt, formatConstants.dateTime, timeZone)}.`}>
                <small className="text-secondary">{time}</small>
              </TooltipPopOver>
            </div>

            <div className="small">
              <HoverMenuContainer>
                <ShowIf condition={!disabled}>
                  <ShowIf condition={!isDeleted && user?._id === by?._id}>
                    <HorizontalStack gap={1}>
                      <DeleteActivity _id={_id} />
                      |
                      <BusyButtonLink
                        icon="edit"
                        title="Edit activity."
                        text="Edit"
                        onClick={() => setShowEdit(true)}
                      />
                    </HorizontalStack>
                  </ShowIf>
                </ShowIf>
              </HoverMenuContainer>
            </div>
          </HorizontalStack>

          <ShowIf condition={!showEdit}>
            <div style={{ whiteSpace: 'pre-line' }} className={deletedTextStyle}>
              <RenderContent activity={isEmpty(activity) ? '--' : activity} />
            </div>
          </ShowIf>
          <ShowIf condition={showEdit}>
            <Stack>
              <UpdateActivity
                activity={{ _id, by, updatedAt, activity, isDeleted }}
                afterSave={() => setShowEdit(false)}
              />
            </Stack>
          </ShowIf>
        </div>
      </div>
    </HoverContainer>
  )
}

const RenderContent = ({ activity }) => {
  // 1. Check if the content is a string
  if (typeof activity === 'string') {
    // trustedSource: activity remarks are authored by staff, not attacker-reachable
    return <RichTextDisplay contentHtml={activity} trustedSource />
  }

  // 2. Otherwise, render it directly as a React element
  // This handles JSX, components, numbers, or null/undefined (which render nothing)
  return <div className="react-element-container">{activity}</div>
}
